import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared";
import { toast } from "react-toastify";
import Grid from "@mui/material/Grid2";
import RoleNames from "./RoleNames";
import RolePermissions from "./RolePermissions";
import "./Roles.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { RoleProvider } from "./RoleContext";
import { useTheme } from "@mui/material";
import { SkeletonLoader } from "../../../modules/dashboard/shared";

const Roles = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [Roles, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState<any>(null);
  const [whiteBoxHeight, setWhiteBoxHeight] = useState<number>(0);
  const whiteBoxRef = useRef<HTMLDivElement>(null);
  const [checkUnsavedChanges, setCheckUnsavedChanges] = useState<() => boolean>(
    () => () => true
  );
  const location = useLocation();
  const lastStateIdRef = useRef<string | null>(null);
  const lastLocationKeyRef = useRef<string | null>(null);
  const handleRoleClick = (role: any) => {
    console.log(role);
    setSelectedRole(role);
  };

  const fetchData = async () => {
    try {
      const roleReq: any = {
        order_column: "name",
        sort_by: "ASC",
      };
      const response = await axiosInstance.post(END_POINTS.ORG_ROLES, roleReq);
      if (response.status === 200) {
        // toast(response.data.message, { type: "success" });
        const result = response.data?.data || [];
        const userCreated = result?.filter((role: any) => {
          return !role?.default_role;
        });
        const systemCreated = result?.filter((role: any) => {
          return role?.default_role;
        });
        const roles = systemCreated?.concat(userCreated);
        setData(roles);
        
        const targetId = location.state?.id;
        if (targetId) {
          const matchedRole = roles.find((r: any) => r.id === targetId);
          if (matchedRole) {
            setSelectedRole(matchedRole);
            lastStateIdRef.current = targetId;
            lastLocationKeyRef.current = location.key;
          } else {
            setSelectedRole(roles[0]);
          }
        } else {
          setSelectedRole(roles[0]);
        }
        
        setLoading(false);
      }
    } catch (error: any) {
      toast.error(
        error.response.data
          ? error.response.data.message
          : "Something went wrong",
        { type: "error" }
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const targetId = location.state?.id;
    const currentKey = location.key;
    if (targetId && Roles.length > 0 && (targetId !== lastStateIdRef.current || currentKey !== lastLocationKeyRef.current)) {
      const matchedRole = Roles.find((r: any) => r.id === targetId);
      if (matchedRole) {
        setSelectedRole(matchedRole);
        lastStateIdRef.current = targetId;
        lastLocationKeyRef.current = currentKey;
      }
    }
  }, [location, Roles]);
  useEffect(() => {
    if (whiteBoxRef.current) {
      const height = whiteBoxRef.current.offsetHeight;
      setWhiteBoxHeight(height);
    }
  }, [loading]);
  const skeletonRowHeight = 25;
  const skeletonRowSpacing = 10;
  const totalRowHeight = skeletonRowHeight + skeletonRowSpacing + 5;
  const skeletonCount = Math.floor(whiteBoxHeight / totalRowHeight);
  if (loading) {
    return (
      <Grid container spacing={2}>
        <Grid size={2}>
          <div
            className={`white-box ${isDarkMode ? "dark-mode" : "light-mode"}`}
            ref={whiteBoxRef}
          >
            {whiteBoxHeight > 0 && (
              <SkeletonLoader
                height={skeletonRowHeight}
                count={skeletonCount}
                style={{ marginBottom: `${skeletonRowSpacing}px` }}
              />
            )}
          </div>
        </Grid>
        <Grid size={10}>
          <div
            className={`white-box ${isDarkMode ? "dark-mode" : "light-mode"}`}
            ref={whiteBoxRef}
          >
            {whiteBoxHeight > 0 && (
              <SkeletonLoader
                height={skeletonRowHeight}
                count={skeletonCount}
                style={{ marginBottom: `${skeletonRowSpacing}px` }}
              />
            )}
          </div>
        </Grid>
      </Grid>
    );
  } else {
    return (
      <RoleProvider>
        <Grid container spacing={2}>
          <Grid size={2}>
            <div
              className={`white-box ${isDarkMode ? "dark-mode" : "light-mode"}`}
            >
              <RoleNames
                Roles={Roles}
                setRoles={setData}
                OnRoleClick={handleRoleClick}
                initialSelectedRole={selectedRole}
                setLoading={setLoading}
                checkUnsavedChanges={checkUnsavedChanges}
              />
            </div>
          </Grid>
          <Grid size={10}>
            <div
              className={`white-box ${isDarkMode ? "dark-mode" : "light-mode"}`}
            >
              <RolePermissions
                role={selectedRole}
                Roles={Roles}
                onCheckUnsavedChanges={setCheckUnsavedChanges}
              />
            </div>
          </Grid>
        </Grid>
      </RoleProvider>
    );
  }
};

export default Roles;
